import { apiClient } from './api';
import { AgentConfig, ProjectContext, AgentResult } from '../models/genericStudio';
import { ResearchStudioService } from './researchStudioService';
import { ResearchProject } from '../models/researchStudio';

export class CreativeStudioAgentService {
  // Map agent types to their API endpoints and methods
  private static agentEndpoints: Record<string, {
    start: string;
    iterate?: string;
    method: 'session' | 'single';
  }> = {
    'abstract-thinker': {
      start: '/api/thinker/abstract/abstract-thinking/start',
      iterate: '/api/thinker/abstract/abstract-thinking/{sessionId}/iterate',
      method: 'session',
    },
    'prompt-improvement': {
      start: '/api/agent/prompt/start',
      iterate: '/api/agent/prompt/{sessionId}/iterate',
      method: 'session',
    },
    'story-generator': {
      start: '/api/agent/story/start',
      iterate: '/api/agent/story/{sessionId}/iterate',
      method: 'session',
    },
    'character': {
      start: '/api/character/start',
      iterate: '/api/character/{sessionId}/iterate',
      method: 'session',
    },
    'scene': {
      start: '/api/scene/start',
      iterate: '/api/scene/{sessionId}/iterate',
      method: 'session',
    },
    'image-prompt': {
      start: '/api/imageprompt/session/start',
      iterate: '/api/imageprompt/session/{sessionId}/iterate',
      method: 'session',
    },
    'storyboard': {
      start: '/api/storyboard/start',
      iterate: '/api/storyboard/{sessionId}/iterate',
      method: 'session',
    },
    'incubation': {
      start: '/api/incubation/start',
      iterate: '/api/incubation/{sessionId}/iterate',
      method: 'session',
    },
    'coding': {
      start: '/api/agent/coding/start',
      iterate: '/api/agent/coding/{sessionId}/iterate',
      method: 'session',
    },
    'image-generation': {
      start: '/api/imagegeneration/generate',
      method: 'single',
    },
    'content-pack': {
      start: '/api/content-packs/prompt-pack/create',
      method: 'single',
    },
    'social-media': {
      start: '/api/multi-agent/social/start',
      method: 'single',
    },
    'blog': {
      start: '/api/blog/multi-agent/start',
      method: 'single',
    },
    'tech-doc': {
      start: '/api/techdoc/multi-agent/start',
      method: 'single',
    },
    'screenplay': {
      start: '/api/screenplay/start',
      iterate: '/api/screenplay/{sessionId}/iterate',
      method: 'session',
    },
    'ux-designer': {
      start: '/api/ux/start',
      iterate: '/api/ux/{sessionId}/iterate',
      method: 'session',
    },
    'urban-culture': {
      start: '/api/urban-agents/{agentType}/start',
      iterate: '/api/urban-agents/{agentType}/{sessionId}/iterate',
      method: 'session',
    },
    'image-analysis': {
      start: '/api/image-analysis/analyze',
      method: 'single',
    },
    'content-marketing': {
      start: '/api/marketing/session/start',
      iterate: '/api/marketing/session/{sessionId}/iterate',
      method: 'session',
    },
    'journalism': {
      start: '/api/journalism/multi-agent/start',
      method: 'single',
    },
    'fashion': {
      start: '/api/fashion/multi-agent/start',
      method: 'single',
    },
    'scientific': {
      start: '/api/quantum-physics/session/start',
      iterate: '/api/quantum-physics/session/{sessionId}/iterate',
      method: 'session',
    },
    'interactive-fiction': {
      start: '/api/interactive-fiction/start',
      iterate: '/api/interactive-fiction/{sessionId}/iterate',
      method: 'session',
    },
    'novel-writing': {
      start: '/api/novel-writing/start',
      iterate: '/api/novel-writing/{sessionId}/iterate',
      method: 'session',
    },
    'simple-data': {
      start: '/api/simple-data/query',
      method: 'single',
    },
    'data-analysis': {
      start: '/api/data-analysis/analyze',
      method: 'single',
    },
    'video-prompt': {
      start: '/api/videoprompt/session/start',
      iterate: '/api/videoprompt/session/{sessionId}/iterate',
      method: 'session',
    },
    // Cuisine Agents
    'cuisine-italian': {
      start: '/api/italian-cuisine/session/start',
      iterate: '/api/italian-cuisine/session/{sessionId}/iterate',
      method: 'session',
    },
    'cuisine-mexican': {
      start: '/api/mexican-cuisine/session/start',
      iterate: '/api/mexican-cuisine/session/{sessionId}/iterate',
      method: 'session',
    },
    'cuisine-indian': {
      start: '/api/indian-cuisine/session/start',
      iterate: '/api/indian-cuisine/session/{sessionId}/iterate',
      method: 'session',
    },
    'cuisine-thai': {
      start: '/api/thai-cuisine/session/start',
      iterate: '/api/thai-cuisine/session/{sessionId}/iterate',
      method: 'session',
    },
    'cuisine-japanese': {
      start: '/api/japanese-cuisine/session/start',
      iterate: '/api/japanese-cuisine/session/{sessionId}/iterate',
      method: 'session',
    },
    'cuisine-french': {
      start: '/api/frenchcuisine/session/start',
      iterate: '/api/frenchcuisine/session/{sessionId}/iterate',
      method: 'session',
    },
    'cuisine-korean': {
      start: '/api/korean-cuisine/session/start',
      iterate: '/api/korean-cuisine/session/{sessionId}/iterate',
      method: 'session',
    },
    'cuisine-chinese': {
      start: '/api/chinesecuisine/session/start',
      iterate: '/api/chinesecuisine/session/{sessionId}/iterate',
      method: 'session',
    },
    'cuisine-spanish': {
      start: '/api/spanishcuisine/session/start',
      iterate: '/api/spanishcuisine/session/{sessionId}/iterate',
      method: 'session',
    },
    'cuisine-caribbean': {
      start: '/api/caribbeancuisine/session/start',
      iterate: '/api/caribbeancuisine/session/{sessionId}/iterate',
      method: 'session',
    },
    'cuisine-general': {
      start: '/api/culinary/session/start',
      iterate: '/api/culinary/session/{sessionId}/iterate',
      method: 'session',
    },
  };

  static async executeAgent(
    agent: AgentConfig,
    context: ProjectContext
  ): Promise<AgentResult> {
    // Check if this is a research agent
    const researchAgentTypes = [
      'ragtag-upload', 'ragtag-query', 'ragtag-summarize',
      'simple-data-query', 'simple-data-summarize',
      'data-analysis-rss', 'data-analysis-csv',
      'abstract-thinking', 'image-analysis',
      'report-generation', 'insight-synthesis'
    ];
    
    if (researchAgentTypes.includes(agent.agentType)) {
      // Delegate to ResearchStudioService
      if ((context.project.type as string) === 'research' || (context.project.type as string) === 'research-report') {
        const researchResult = await ResearchStudioService.executeAgent(agent, context);
        // Convert research result to standard AgentResult format
        return {
          agentType: researchResult.agentType,
          agentName: agent.agentName,
          success: researchResult.success,
          data: researchResult.data,
          error: researchResult.error,
          metadata: {
            executedAt: new Date().toISOString(),
            config: agent.config,
          },
        };
      }
    }

    const endpoint = this.agentEndpoints[agent.agentType];
    if (!endpoint) {
      throw new Error(`Unknown agent type: ${agent.agentType}`);
    }

    try {
      if (endpoint.method === 'session') {
        return await this.executeSessionAgent(agent, context, endpoint);
      } else {
        return await this.executeSingleAgent(agent, context, endpoint);
      }
    } catch (error: any) {
      console.error(`Agent execution failed for ${agent.agentName}:`, error);
      throw error;
    }
  }

  private static async executeSessionAgent(
    agent: AgentConfig,
    context: ProjectContext,
    endpoint: { start: string; iterate?: string }
  ): Promise<AgentResult> {
    // Build request based on agent type and context
    const startRequest = this.buildAgentRequest(agent, context);
    
    // Start session
    const startUrl = this.resolveUrl(endpoint.start, agent);
    const startResponse = await apiClient.post(startUrl, startRequest);
    
    if (!startResponse.data.sessionId) {
      throw new Error('No session ID returned from agent');
    }

    const sessionId = startResponse.data.sessionId;
    
    // Store session in project context
    context.project.agentSessions.set(agent.agentType, {
      sessionId,
      agentType: agent.agentType,
      startedAt: new Date().toISOString(),
    });

    // If iterate endpoint exists and we need iterations
    if (endpoint.iterate && agent.config?.iterations) {
      const iterations = agent.config.iterations || 1;
      let lastResult = startResponse.data;
      
      for (let i = 0; i < iterations; i++) {
        const iterateUrl = this.resolveUrl(endpoint.iterate, agent, sessionId);
        const iterateRequest = this.buildIterationRequest(agent, context, lastResult);
        
        const iterateResponse = await apiClient.post(iterateUrl, iterateRequest);
        lastResult = iterateResponse.data;
      }
      
      return this.processAgentResponse(agent, lastResult);
    }

    return this.processAgentResponse(agent, startResponse.data);
  }

  private static async executeSingleAgent(
    agent: AgentConfig,
    context: ProjectContext,
    endpoint: { start: string }
  ): Promise<AgentResult> {
    const request = this.buildAgentRequest(agent, context);
    const url = this.resolveUrl(endpoint.start, agent);
    
    const response = await apiClient.post(url, request);
    return this.processAgentResponse(agent, response.data);
  }

  private static buildAgentRequest(agent: AgentConfig, context: ProjectContext): any {
    // Get previous results that this agent depends on
    const dependencies: Record<string, any> = {};
    if (agent.dependencies) {
      agent.dependencies.forEach(dep => {
        const result = context.previousResults.get(dep);
        if (result) {
          dependencies[dep] = result.data;
        }
      });
    }

    // Build request based on agent type
    switch (agent.agentType) {
      case 'abstract-thinker':
        return {
          initialQueryOrTopic: context.userInputs.concept || context.project.description || 'Creative project exploration',
          depth: agent.config?.depth || 'deep',
          temperature: agent.config?.temperature || 0.7,
          focusArea: agent.config?.focus || 'creative',
        };

      case 'prompt-improvement':
        const abstractResult = dependencies['abstract-thinker'];
        return {
          prompt: abstractResult?.thought || abstractResult?.initialThought || context.project.description,
          targetModel: 'gpt-4',
          improvementType: 'clarity',
        };

      case 'story-generator':
        return {
          concept: context.userInputs.storyConcept || dependencies['abstract-thinker']?.thought || context.project.description,
          genre: agent.config?.genre || 'general',
          tone: agent.config?.tone || 'balanced',
          mode: agent.config?.mode || 'single',
        };

      case 'character':
        const storyData = dependencies['story-generator'];
        return {
          initialPrompt: context.userInputs.characterName || storyData?.story || context.project.description,
          name: context.userInputs.characterName || 'Main Character',
          role: context.userInputs.characterRole || 'protagonist',
          context: storyData?.story || storyData?.initialStory || context.project.description,
          traits: [],
        };

      case 'scene':
        const characterData = dependencies['character'];
        return {
          initialPrompt: context.userInputs.sceneTitle || dependencies['story-generator']?.story || context.project.description,
          title: context.userInputs.sceneTitle || 'Opening Scene',
          location: context.userInputs.location || 'Unknown Location',
          time: context.userInputs.timeOfDay || 'day',
          characters: characterData ? [characterData] : [],
          mood: 'neutral',
        };

      case 'image-prompt':
        return {
          initialPrompt: dependencies['scene']?.description || dependencies['abstract-thinker']?.thought || context.project.description,
          style: agent.config?.style || 'realistic',
          aspectRatio: '16:9',
        };

      case 'storyboard':
        const sceneResult = dependencies['scene'];
        return {
          initialPrompt: context.project.description,
          projectTitle: context.project.name,
          projectType: 'animation',
          scenes: sceneResult ? [sceneResult] : [],
          style: 'cinematic',
        };

      case 'incubation':
        return {
          systemName: context.project.name,
          description: dependencies['abstract-thinker']?.thought || context.project.description,
          systemType: agent.config?.systemType || 'web-app',
          requirements: [],
        };

      case 'coding':
        const incubationData = dependencies['incubation'];
        return {
          task: agent.config?.task || 'implement',
          language: 'typescript',
          framework: 'react',
          context: incubationData?.architecture || context.project.description,
          mode: agent.config?.mode || 'multi-agent',
        };

      case 'image-generation':
        const promptData = dependencies['image-prompt'];
        return {
          prompt: promptData?.improvedPrompt || promptData?.prompt || context.project.description,
          model: agent.config?.model || 'flux-pro',
          width: 1024,
          height: 1024,
          numImages: agent.config?.batchSize || 1,
        };

      case 'content-pack':
        return {
          title: context.project.name + ' Content Pack',
          description: context.project.description,
          type: agent.config?.packType || 'prompt',
          prompts: context.previousResults.get('image-prompt')?.data?.prompts || [],
          targetModels: ['flux', 'midjourney', 'stable-diffusion'],
        };

      case 'social-media':
        return {
          brandName: context.project.name,
          campaignGoals: agent.config?.goals || context.project.metadata.goals || [],
          targetAudience: agent.config?.audience || 'general',
          platforms: agent.config?.platforms || ['instagram', 'twitter'],
          contentIdeas: dependencies['abstract-thinker']?.insights || [],
        };

      case 'blog':
        return {
          topic: dependencies['abstract-thinker']?.thought || context.project.description,
          style: 'informative',
          length: 'medium',
          includeImages: true,
        };

      case 'tech-doc':
        const codingData = dependencies['coding'];
        return {
          projectName: context.project.name,
          codeContext: codingData?.code || '',
          docTypes: agent.config?.docTypes || ['api', 'user-guide'],
          format: 'markdown',
        };

      case 'scientific':
        return {
          query: context.project.description,
          domain: agent.config?.domain || 'Quantum Physics',
          detailDomain: agent.config?.detailDomain || 'General Concepts',
        };

      // Cuisine agents
      case 'cuisine-italian':
      case 'cuisine-mexican':
      case 'cuisine-indian':
      case 'cuisine-thai':
      case 'cuisine-japanese':
      case 'cuisine-french':
      case 'cuisine-korean':
      case 'cuisine-chinese':
      case 'cuisine-spanish':
      case 'cuisine-caribbean':
      case 'cuisine-general':
        return {
          initialPrompt: context.userInputs.recipeIdea || context.project.description || 'Create an authentic dish',
          sessionType: agent.agentType.replace('cuisine-', ''),
          temperature: agent.config?.temperature || 0.7,
          includeTraditionalElements: agent.config?.includeTraditionalElements !== false,
          includeModernInnovations: agent.config?.includeModernInnovations || false,
          emphasizeAuthenticity: agent.config?.emphasizeAuthenticity !== false,
          dietaryConsiderations: agent.config?.dietaryConsiderations || [],
          requiredIngredients: agent.config?.requiredIngredients || [],
        };

      default:
        return {
          input: context.project.description,
          config: agent.config || {},
        };
    }
  }

  private static buildIterationRequest(agent: AgentConfig, context: ProjectContext, previousResult: any): any {
    // Build iteration request based on agent type
    switch (agent.agentType) {
      case 'abstract-thinker':
        return {
          direction: 'explore deeper',
          focusArea: agent.config?.focus || 'creative expansion',
        };

      case 'story-generator':
        return {
          feedback: 'continue story',
          direction: 'develop plot',
        };

      case 'image-prompt':
        return {
          agentType: 'Creative',
          additionalInstructions: 'enhance visual details',
        };

      case 'scientific':
        return {
          userInput: 'explore further scientific implications',
          domain: 'Applied Physics',
          detailDomain: 'Theoretical Framework',
        };

      // Cuisine agents
      case 'cuisine-italian':
      case 'cuisine-mexican':
      case 'cuisine-indian':
      case 'cuisine-thai':
      case 'cuisine-japanese':
      case 'cuisine-french':
      case 'cuisine-korean':
      case 'cuisine-chinese':
      case 'cuisine-spanish':
      case 'cuisine-caribbean':
      case 'cuisine-general':
        return {
          temperature: agent.config?.temperature || 0.7,
          includeTraditionalElements: agent.config?.includeTraditionalElements !== false,
          includeModernInnovations: agent.config?.includeModernInnovations || false,
          emphasizeAuthenticity: agent.config?.emphasizeAuthenticity !== false,
          userInput: context.userInputs.feedback || 'refine the recipe',
        };

      default:
        return {
          feedback: 'continue',
          options: {},
        };
    }
  }

  private static processAgentResponse(agent: AgentConfig, response: any): AgentResult {
    // Process and normalize response based on agent type
    let processedData: any = {};

    switch (agent.agentType) {
      case 'abstract-thinker':
        // Check if response has thinkingProcess array (as shown in user's example)
        if (response.thinkingProcess && Array.isArray(response.thinkingProcess)) {
          // Extract the final conclusion and all critic outputs
          const criticOutputs = response.thinkingProcess
            .filter((item: any) => item.output)
            .map((item: any) => item.output)
            .join('\n\n');
          
          processedData = {
            thought: response.finalConclusion || criticOutputs || response.thought || response.expandedThought || response.initialThought,
            insights: response.insights || [],
            concepts: response.concepts || [],
            sessionId: response.sessionId,
            thinkingProcess: response.thinkingProcess,
            finalConclusion: response.finalConclusion,
            criticOutputs: criticOutputs,
          };
        } else {
          // Fallback for older response format
          processedData = {
            thought: response.thought || response.expandedThought || response.initialThought,
            insights: response.insights || [],
            concepts: response.concepts || [],
            sessionId: response.sessionId,
          };
        }
        break;

      case 'story-generator':
        processedData = {
          story: response.story || response.initialStory,
          characters: response.characters || [],
          plot: response.plot || {},
          sessionId: response.sessionId,
        };
        break;

      case 'character':
        processedData = {
          character: response.character || response,
          traits: response.traits || [],
          backstory: response.backstory || '',
          sessionId: response.sessionId,
        };
        break;

      case 'scene':
        processedData = {
          scene: response.scene || response,
          description: response.description || '',
          atmosphere: response.atmosphere || {},
          sessionId: response.sessionId,
        };
        break;

      case 'image-prompt':
        processedData = {
          prompt: response.improvedPrompt || response.processedPrompt,
          originalPrompt: response.originalPrompt,
          improvements: response.improvements || [],
          sessionId: response.sessionId,
        };
        break;

      case 'image-generation':
        processedData = {
          images: response.images || [response.imageUrl],
          prompt: response.prompt,
          model: response.model,
        };
        break;

      case 'social-media':
        processedData = {
          campaign: response.campaign || response,
          posts: response.posts || [],
          strategy: response.strategy || {},
        };
        break;

      case 'scientific':
        const visualization = response.visualization || response;
        processedData = {
          visualization: visualization,
          analysis: visualization?.content?.detailedExplanation || '',
          insights: visualization?.content?.keyInsights || [],
          sessionId: response.sessionId,
        };
        break;

      // Cuisine agents
      case 'cuisine-italian':
      case 'cuisine-mexican':
      case 'cuisine-indian':
      case 'cuisine-thai':
      case 'cuisine-japanese':
      case 'cuisine-french':
      case 'cuisine-korean':
      case 'cuisine-chinese':
      case 'cuisine-spanish':
      case 'cuisine-caribbean':
      case 'cuisine-general':
        processedData = {
          recipe: response.recipe || null,
          culturalContext: response.culturalContext || '',
          nutritionalInfo: response.nutritionalInfo || null,
          cookingTips: response.cookingTips || [],
          variations: response.variations || [],
          sessionId: response.sessionId,
          success: response.success || true,
        };
        break;

      default:
        processedData = response;
    }

    return {
      agentType: agent.agentType,
      agentName: agent.agentName,
      success: true,
      data: processedData,
      metadata: {
        executedAt: new Date().toISOString(),
        config: agent.config,
      },
    };
  }

  private static resolveUrl(template: string, agent: AgentConfig, sessionId?: string): string {
    let url = template;
    
    // Replace placeholders
    if (sessionId) {
      url = url.replace('{sessionId}', sessionId);
    }
    
    if (agent.agentType === 'urban-culture' && agent.config?.subType) {
      url = url.replace('{agentType}', agent.config.subType);
    }
    
    return url;
  }

  // Helper method to extract artifact from agent result
  static extractArtifacts(agentResult: AgentResult): any[] {
    const artifacts: any[] = [];
    
    // Check if this is a research agent result
    const researchAgentTypes = [
      'ragtag-upload', 'ragtag-query', 'ragtag-summarize',
      'simple-data-query', 'simple-data-summarize',
      'data-analysis-rss', 'data-analysis-csv',
      'abstract-thinking', 'image-analysis',
      'report-generation', 'insight-synthesis'
    ];
    
    if (researchAgentTypes.includes(agentResult.agentType)) {
      // Delegate to ResearchStudioService for artifact extraction
      return ResearchStudioService.extractArtifacts(agentResult as any);
    }
    
    switch (agentResult.agentType) {
      case 'image-generation':
        if (agentResult.data.images) {
          agentResult.data.images.forEach((url: string, i: number) => {
            artifacts.push({
              id: `img-${Date.now()}-${i}`,
              type: 'image',
              name: `Generated Image ${i + 1}`,
              url,
              createdBy: agentResult.agentName,
            });
          });
        }
        break;

      case 'story-generator':
        if (agentResult.data.story) {
          artifacts.push({
            id: `story-${Date.now()}`,
            type: 'text',
            name: 'Generated Story',
            content: agentResult.data.story,
            createdBy: agentResult.agentName,
          });
        }
        break;

      case 'content-pack':
        if (agentResult.data.packId) {
          artifacts.push({
            id: agentResult.data.packId,
            type: 'content-pack',
            name: agentResult.data.title || 'Content Pack',
            data: agentResult.data,
            createdBy: agentResult.agentName,
          });
        }
        break;

      case 'abstract-thinker':
        // Extract artifacts from abstract thinker response
        if (agentResult.data.finalConclusion || agentResult.data.thought || agentResult.data.criticOutputs) {
          artifacts.push({
            id: `abstract-${Date.now()}`,
            type: 'text',
            name: 'Abstract Thinking Analysis',
            content: agentResult.data.finalConclusion || agentResult.data.thought,
            createdBy: agentResult.agentName,
            metadata: {
              thinkingProcess: agentResult.data.thinkingProcess,
              criticOutputs: agentResult.data.criticOutputs,
            },
          });
        }
        break;

      // Cuisine agents
      case 'cuisine-italian':
      case 'cuisine-mexican':
      case 'cuisine-indian':
      case 'cuisine-thai':
      case 'cuisine-japanese':
      case 'cuisine-french':
      case 'cuisine-korean':
      case 'cuisine-chinese':
      case 'cuisine-spanish':
      case 'cuisine-caribbean':
      case 'cuisine-general':
        if (agentResult.data.recipe) {
          artifacts.push({
            id: `recipe-${Date.now()}`,
            type: 'recipe',
            name: agentResult.data.recipe.name || 'Recipe',
            content: JSON.stringify(agentResult.data.recipe, null, 2),
            createdBy: agentResult.agentName,
            metadata: {
              culturalContext: agentResult.data.culturalContext,
              nutritionalInfo: agentResult.data.nutritionalInfo,
              cookingTips: agentResult.data.cookingTips,
              variations: agentResult.data.variations,
            },
          });
        }
        break;
    }
    
    return artifacts;
  }
}